/**
 * @file        src/pages/enquiry/GroupRequestList.jsx
 * @description Query Management → Group Booking Requests. Every "Flight
 *              Request" a customer submitted on the storefront (the GET QUOTE /
 *              group booking form), with the itinerary they want priced, the
 *              number they left, and a pipeline state so two people do not
 *              quote the same party twice.
 *
 *              BEFORE THIS SCREEN THE REQUEST'S ONLY RECORD WAS AN EMAIL.
 *              booking_backend mailed the brand's support inbox and persisted
 *              nothing, so an undelivered send, an unset mailbox or a deleted
 *              message lost the lead with no trace anywhere in the console —
 *              while the customer had been told a specialist would come back
 *              within 12–24 hours. Migration 073 added the table; this reads it.
 *
 *              WHOLE-BUSINESS, not a B2C / Corporate pair (§9d, the Transaction
 *              Log and Abandoned Payments exception). The form is reachable
 *              without signing in and most group enquiries come from a guest,
 *              so an audience split would file the majority of leads under
 *              neither screen. "Customer" is a read-only, filterable column
 *              instead — it says who is calling without pretending the table
 *              can be partitioned.
 *
 *              THE CARDS AND THE TABLE ARE ONE QUERY. /api/group-requests
 *              returns its totals in `meta.totals`, computed from the same
 *              filtered statement, so a card can never count a set the rows
 *              beneath it do not show.
 * @author      Kinetika Labs
 * @created     2026-09-25
 * @changelog
 *   - 2026-09-25 Claude — Initial.
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Select from '../../components/Select';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { useBrands } from '../../api/brands';
import { qk } from '../../api/queryKeys';
import { listGroupRequests, getGroupRequest, deleteGroupRequest } from '../../api/groupRequests';
import { formatDate, formatDateTime } from '../../lib/format';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import GroupRequestDetailModal from './GroupRequestDetailModal';
import {
  STATUS_FILTER_OPTIONS, STATUS_LABEL, STATUS_TONE,
  TRIP_OPTIONS, TRIP_LABEL, CUSTOMER_OPTIONS, CUSTOMER_LABEL, paxLine,
} from './groupRequestMeta';

const NOTIFIED_OPTIONS = [
  { value: '', label: 'All' },
  // The reason the table exists: the notification could always fail, and before
  // it there was nothing left behind when it did.
  { value: 'false', label: 'Not emailed' },
  { value: 'true', label: 'Emailed' },
];

export default function GroupRequestList() {
  const { options: brandOptions, labelOf } = useBrands();
  const qc = useQueryClient();

  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [brand, setBrand] = useState('');
  // Defaults to the working queue, not the whole archive — this screen is a
  // call list, and "every quote we have ever been asked for" is not one.
  const [status, setStatus] = useState('new');
  const [tripType, setTripType] = useState('');
  const [customerType, setCustomerType] = useState('');
  const [notified, setNotified] = useState('');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [detailRow, setDetailRow] = useState(null);

  // ?open=<id> opens that request straight away — the link the Action Centre
  // sends, so "quote the trip" lands on the lead rather than on a list with the
  // customer's name still to find. It is fetched BY ID rather than looked up in
  // the page: this screen defaults to the new-leads filter, so the row being
  // linked to is often not in the current page at all. Seeded once; closing the
  // modal clears the param so a refresh does not reopen what was dismissed.
  // `open` never reaches the API — it selects a row, it does not filter.
  const [urlParams, setUrlParams] = useSearchParams();
  const openId = Number(urlParams.get('open'));
  const { data: linked } = useQuery({
    queryKey: qk.groupRequests.one(openId),
    queryFn: () => getGroupRequest(openId),
    enabled: Number.isInteger(openId) && openId > 0,
  });
  const openRow = detailRow || linked?.data || null;
  const closeDetail = () => {
    setDetailRow(null);
    if (urlParams.get('open')) {
      const next = new URLSearchParams(urlParams);
      next.delete('open');
      setUrlParams(next, { replace: true });
    }
  };

  const brandFilterOptions = [{ value: '', label: 'All brands' }, ...brandOptions];
  const brandLabel = (b) => (b ? labelOf(b) : '—');

  const params = {
    page,
    pageSize,
    ...(brand ? { brand } : {}),
    ...(status ? { status } : {}),
    ...(tripType ? { tripType } : {}),
    ...(customerType ? { customerType } : {}),
    ...(notified ? { notified } : {}),
    ...(appliedSearch ? { search: appliedSearch } : {}),
    ...(from ? { from } : {}),
    // Inclusive end-of-day — a bare date compares as 00:00:00 and would
    // silently drop every request from the "to" day itself.
    ...(to ? { to: `${to} 23:59:59.999` } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: qk.groupRequests.list(params),
    queryFn: () => listGroupRequests(params),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => deleteGroupRequest(id),
    onSuccess: () => {
      toast.ok('Deleted', 'Request removed.');
      qc.invalidateQueries({ queryKey: ['group-requests'] });
    },
    onError: (e) => toast.err('Failed', e.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const meta = data?.meta || {};
  const t = meta.totals;
  const total = meta.total || 0;

  function onSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    setAppliedSearch(search.trim());
  }
  const applyFilter = (setter) => (v) => { setter(v); setPage(1); };

  async function onDelete(row) {
    if (!(await confirm(`Delete the group request from "${row.name}"?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Group Booking Requests"
        subtitle="Query Management · quote requests from the storefront's Flight Request form — call them back and price the trip"
      />

      <div className="mini-kpi">
        <article className="mk glass neo">
          <small>New</small><b>{t ? t.new : '—'}</b>
        </article>
        <article className="mk glass neo">
          <small>In Progress</small><b>{t ? t.working : '—'}</b>
        </article>
        <article className="mk glass neo">
          <small>Won</small><b className="cre">{t ? t.won : '—'}</b>
        </article>
        {/* A lead NOBODY was emailed about. The row is the only reason it still
            exists, so a non-zero here is the one number on this strip that
            means something is wrong rather than something is pending. */}
        <article className="mk glass neo">
          <small>Not Emailed</small><b>{t ? t.notNotified : '—'}</b>
        </article>
      </div>

      <article className="panel glass">
        <form className="search-row" onSubmit={onSearchSubmit}>
          <div className="field">
            <label>Name / Email / Mobile / Route</label>
            <input placeholder="Search requests…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="field">
            <label>Status</label>
            <Select value={status} onChange={applyFilter(setStatus)} options={STATUS_FILTER_OPTIONS} />
          </div>
          <div className="field">
            <label>Trip</label>
            <Select value={tripType} onChange={applyFilter(setTripType)} options={TRIP_OPTIONS} />
          </div>
          <div className="field">
            <label>Customer</label>
            <Select value={customerType} onChange={applyFilter(setCustomerType)} options={CUSTOMER_OPTIONS} />
          </div>
          <div className="field">
            <label>Brand</label>
            <Select value={brand} onChange={applyFilter(setBrand)} options={brandFilterOptions} />
          </div>
          <div className="field">
            <label>Notification</label>
            <Select value={notified} onChange={applyFilter(setNotified)} options={NOTIFIED_OPTIONS} />
          </div>
          <div className="field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr>
                  <th>#</th><th>Customer</th><th>Contact</th><th>Trip</th><th>Route</th>
                  <th>Travel date</th><th>Pax</th><th>Brand</th><th>Received</th>
                  <th>Status</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={11} className="empty">No group booking requests match these filters.</td></tr>
                ) : rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>
                      <b>{r.name}</b>
                      <br /><span className="muted">{CUSTOMER_LABEL[r.customerType] || r.customerType}</span>
                    </td>
                    <td>
                      <a href={`tel:${r.mobile}`} className="mono">{r.mobile}</a>
                      <br /><a href={`mailto:${r.email}`} className="muted">{r.email}</a>
                    </td>
                    <td>
                      {TRIP_LABEL[r.tripType] || r.tripType}
                      {r.legCount > 1 ? <><br /><span className="muted">{r.legCount} legs</span></> : null}
                    </td>
                    <td className="mono">{r.origin || '—'} → {r.destination || '—'}</td>
                    <td>{r.travelDate ? formatDate(r.travelDate) : '—'}</td>
                    <td className="mono">{paxLine(r.pax) || '—'}</td>
                    <td className="mono">{(r.brand || '—').toUpperCase()}</td>
                    <td>{formatDateTime(r.createdAt)}</td>
                    <td>
                      <Chip tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status] || r.status}</Chip>
                      {/* Only shown when it FAILED. A green "emailed" badge on
                          every row would bury the handful that matter. */}
                      {r.notified ? null : <><br /><Chip tone="warn">Not emailed</Chip></>}
                    </td>
                    <td className="row-act">
                      <button className="ib" aria-label="Open" title="Open" onClick={() => setDetailRow(r)}>
                        <Icon name="eye" />
                      </button>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(r)}>
                        <Icon name="trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>

      {openRow ? (
        <GroupRequestDetailModal
          row={openRow}
          brandLabel={brandLabel}
          onClose={closeDetail}
        />
      ) : null}
    </>
  );
}
